export type MasterSelectOption = { label: string; value: string; desc?: string };

/** Income slab master codes are two-digit strings (31–36). */
const isIncomeSlabCode = (value: string) => /^\d{2}$/.test(value.trim());

/** Occupation master codes are numeric strings up to 2 characters (e.g. 3, 11). */
const isOccupationCode = (value: string) => {
  const trimmed = value.trim();
  return trimmed.length > 0 && trimmed.length <= 2 && /^\d+$/.test(trimmed);
};

export const resolveIncomeSlabCode = (
  raw: string | undefined | null,
  incomeSlab: MasterSelectOption[],
): string => {
  const value = (raw || "").trim();
  if (!value) return "";
  if (isIncomeSlabCode(value)) return value;

  const byValue = incomeSlab.find((o) => o.value === value);
  if (byValue) return byValue.value;

  const lower = value.toLowerCase();
  const byLabel = incomeSlab.find((o) => o.label.toLowerCase() === lower);
  if (byLabel) return byLabel.value;

  const compact = lower.replace(/[₹,\s]/g, "");
  const byFuzzyLabel = incomeSlab.find((o) => {
    const labelCompact = o.label.toLowerCase().replace(/[₹,\s]/g, "");
    return (
      labelCompact === compact ||
      labelCompact.includes(compact) ||
      compact.includes(labelCompact)
    );
  });
  if (byFuzzyLabel) return byFuzzyLabel.value;

  if (/1.*5.*lak/i.test(value)) {
    const slab = incomeSlab.find(
      (o) => /1/.test(o.label) && /5/.test(o.label) && /lak/i.test(o.label),
    );
    if (slab) return slab.value;
  }

  return value;
};

const findOccupationOption = (candidate: string, occupations: MasterSelectOption[]) => {
  const normalized = candidate.trim();
  if (!normalized) return undefined;

  return occupations.find(
    (o) =>
      String(o.value) === normalized || String(o.value) === String(Number(normalized)),
  );
};

export const resolveOccupationCode = (
  raw: string | number | undefined | null,
  occupations: MasterSelectOption[],
): string => {
  const value = String(raw ?? "").trim();
  if (!value) return "";

  if (isOccupationCode(value)) {
    const byCode = findOccupationOption(value, occupations);
    if (byCode) return String(byCode.value);
  }

  const byValue = findOccupationOption(value, occupations);
  if (byValue) return String(byValue.value);

  const normalized = value.toLowerCase().replace(/\s+/g, "");
  const byLabel = occupations.find((o) => {
    const labelNorm = o.label.toLowerCase().replace(/\s+/g, "");
    return (
      labelNorm === normalized ||
      o.label.toLowerCase() === value.toLowerCase() ||
      labelNorm.includes(normalized) ||
      normalized.includes(labelNorm)
    );
  });
  if (byLabel) return String(byLabel.value);

  return "";
};

type NullableString = string | null | undefined;

export type KycIncomeFields = {
  income?: NullableString;
  grossAnnualIncome?: NullableString;
};

export type KycOccupationFields = {
  occupation?: NullableString | number;
};

/** Prefer KYC form values, then applicant/joint-holder fields (income or grossAnnualIncome). */
export const resolveHolderIncomeCode = (
  kyc: KycIncomeFields | undefined,
  holder: KycIncomeFields | undefined,
  incomeSlab: MasterSelectOption[],
): string =>
  resolveIncomeSlabCode(
    kyc?.income || holder?.income || holder?.grossAnnualIncome,
    incomeSlab,
  );

/**
 * Resolves a raw income value to the master `Desc` string (e.g. "> 1 <= 5 Lac").
 * The combined-checkout API expects grossAnnualIncome as the Desc, not the Code.
 * Resolves to the Code first (reusing resolveIncomeSlabCode), then maps that Code
 * to its Desc via the income-slab master. Falls back to the resolved value when
 * no Desc is available (older master data without desc), so nothing is lost.
 */
export const resolveIncomeSlabDesc = (
  raw: string | undefined | null,
  incomeSlab: MasterSelectOption[],
): string => {
  const code = resolveIncomeSlabCode(raw, incomeSlab);
  if (!code) return "";
  const match = incomeSlab.find((o) => o.value === code);
  return match?.desc || code;
};

/** Desc variant of resolveHolderIncomeCode for the combined-checkout payload. */
export const resolveHolderIncomeDesc = (
  kyc: KycIncomeFields | undefined,
  holder: KycIncomeFields | undefined,
  incomeSlab: MasterSelectOption[],
): string =>
  resolveIncomeSlabDesc(
    kyc?.income || holder?.income || holder?.grossAnnualIncome,
    incomeSlab,
  );

export const resolveHolderOccupationCode = (
  kyc: KycOccupationFields | undefined,
  holder: KycOccupationFields | undefined,
  occupations: MasterSelectOption[],
): string => resolveOccupationCode(kyc?.occupation || holder?.occupation, occupations);
